import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import {
  ArrowRight,
  Cloud,
  Heart,
  MessageSquare,
  Shield,
  Sparkles,
} from "lucide-react";
import { Button } from "../components/ui/button";
import Healthcare from "../sections/what-we-do/Healthcare";
import {
  AITransformationServices,
  AdvisoryServices,
  CloudServices,
  CyberSecurity,
  ClientOutcomes,
  ServiceNav,
} from "../sections/what-we-do";

const fade = {
  hidden: { opacity: 0, y: 22 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.15 },
  },
};

const services = [
  {
    id: "ai-transformation",
    title: "AI Transformation Services",
    navLabel: "AI Transformation",
    summary:
      "Strategy-to-production AI programs for healthcare workflows with governance, integration, and measurable outcomes.",
    icon: Sparkles,
    component: AITransformationServices,
  },
  {
    id: "healthcare",
    title: "Healthcare IT Services",
    navLabel: "Healthcare IT",
    summary:
      "Managed services, implementation, infrastructure, analytics, and compliance reporting delivered by healthcare operators.",
    icon: Heart,
    component: Healthcare,
  },
  {
    id: "cybersecurity",
    title: "Cyber Security Solutions",
    navLabel: "Cyber Security",
    summary:
      "Assessments, 24×7 defense, and IoMT protection aligned to NIST and HIPAA.",
    icon: Shield,
    component: CyberSecurity,
  },
  {
    id: "cloud",
    title: "Cloud Services",
    navLabel: "Cloud",
    summary:
      "Cloud advisory, migration, and modernization across healthcare-critical workloads.",
    icon: Cloud,
    component: CloudServices,
  },
  {
    id: "advisory",
    title: "Advisory Services",
    navLabel: "Advisory",
    summary:
      "Operator-led advisory for strategy, M&A, cost optimization, and system selection.",
    icon: MessageSquare,
    component: AdvisoryServices,
  },
];

function MidInlineCta({ kicker, title, body, cta }) {
  return (
    <section className="bg-white px-4 py-10 lg:px-8 lg:py-12">
      <div className="mx-auto max-w-6xl">
        <div className="flex flex-col items-start gap-5 rounded-2xl border border-shark-200 bg-shark-50/60 px-6 py-7 sm:flex-row sm:items-center sm:justify-between sm:px-8">
          <div className="max-w-2xl">
            <p className="text-[10.5px] font-bold uppercase tracking-[0.14em] text-complement-600">
              {kicker}
            </p>
            <h3 className="mt-1.5 text-[1.1rem] font-bold leading-snug text-shark-900 sm:text-[1.2rem]">
              {title}
            </h3>
            <p className="mt-2 text-[0.9rem] leading-relaxed text-shark-500">
              {body}
            </p>
          </div>
          <Button
            asChild
            className="h-11 shrink-0 px-6 text-[13px] font-bold"
            style={{ backgroundColor: "#e76d38" }}
          >
            <Link to="/contact">
              {cta}
              <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
}

export default function WhatWeDo() {
  const reduceMotion = useReducedMotion();

  return (
    <>
      <Helmet>
        <title>
          What We Do | Velozent — Healthcare IT, AI, Cyber, Cloud & Advisory
        </title>
        <meta
          name="description"
          content="Five healthcare IT service lines proven across 15 hospitals and 250+ clinics: AI transformation, healthcare IT, cyber security, cloud, and advisory—delivered by former CIOs, CISOs, and CTOs."
        />
        <meta
          name="keywords"
          content="healthcare IT services, AI transformation, cyber security, cloud services, advisory services, healthcare technology, Velozent"
        />
        <meta
          property="og:title"
          content="What We Do | Velozent — Healthcare IT, AI, Cyber, Cloud & Advisory"
        />
        <meta
          property="og:description"
          content="Five healthcare IT service lines proven across 15 hospitals and 250+ clinics. Operator-led delivery, production-ready outcomes."
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://velozent.com/what-we-do" />
        <link rel="canonical" href="https://velozent.com/what-we-do" />
      </Helmet>

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-shark-950 px-4 pb-16 pt-20 text-white lg:pb-20 lg:pt-24">
        <div className="hero-mesh">
          <div className="hero-mesh-orb hero-mesh-orb--1" />
          <div className="hero-mesh-orb hero-mesh-orb--2" />
          <div className="hero-mesh-orb hero-mesh-orb--3" />
          <div className="hero-mesh-grain" />
        </div>

        <div className="relative z-10 mx-auto max-w-5xl">
          <motion.div
            variants={stagger}
            initial="hidden"
            animate="visible"
            className="text-center"
          >
            <motion.p
              variants={fade}
              className="text-[11px] font-bold uppercase tracking-[0.22em] text-burnt-sienna-400"
            >
              What we do
            </motion.p>

            <motion.h1
              variants={fade}
              className="mx-auto mt-4 max-w-4xl text-balance text-[clamp(2rem,4.6vw,3.15rem)] font-extrabold leading-[1.08] tracking-tight text-white"
            >
              Five healthcare IT service lines,{" "}
              <span className="bg-gradient-to-r from-burnt-sienna-300 via-burnt-sienna-400 to-burnt-sienna-500 bg-clip-text text-transparent">
                one operator-run delivery model
              </span>
            </motion.h1>

            <motion.p
              variants={fade}
              className="mx-auto mt-5 max-w-2xl text-[1.05rem] leading-relaxed text-white/75"
            >
              From AI transformation and EHR operations to cyber, cloud, and
              C-suite advisory—built by former CIOs, CISOs, and CTOs and proven
              across 15 hospitals and 250+ clinics.
            </motion.p>

            <motion.div
              variants={fade}
              className="mt-10 flex flex-wrap items-center justify-center gap-4"
            >
              <Button
                asChild
                size="lg"
                className="group h-12 border-0 px-8 text-[14px] font-bold text-white shadow-[0_8px_36px_-8px_rgba(231,109,56,0.75)] transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-[0_14px_44px_-8px_rgba(231,109,56,0.55)]"
                style={{ backgroundColor: "#e76d38" }}
              >
                <Link to="/contact">
                  Book Your 15-Minute IT Strategy Call
                  <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Link>
              </Button>
              <a
                href="#ai-transformation"
                className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-white/60 transition-colors hover:text-white"
              >
                See services below
                <ArrowRight className="h-3.5 w-3.5" />
              </a>
            </motion.div>

            <motion.p
              variants={fade}
              className="mt-5 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/40"
            >
              No obligation · No pitch deck · 15-minute call
            </motion.p>
          </motion.div>
        </div>

        <div className="pointer-events-none absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-burnt-sienna-500/50 to-transparent" />
      </section>

      {/* ── Sticky service nav ──────────────────────────────────────────── */}
      <ServiceNav services={services} />

      {/* ── Service sections with interleaved mid-CTAs ──────────────────── */}
      {services.map((service, index) => {
        const SectionComponent = service.component;
        const showMidCta = index === 1 || index === 3;
        return (
          <div key={service.id}>
            <motion.section
              id={service.id}
              className="scroll-mt-[112px] lg:scroll-mt-[140px]"
              initial={reduceMotion ? false : { opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            >
              <SectionComponent />
            </motion.section>

            {showMidCta && (
              <MidInlineCta
                kicker={
                  index === 1 ? "Not sure where to start?" : "Planning a bigger move?"
                }
                title={
                  index === 1
                    ? "Talk to an operator before the RFP"
                    : "Need a cost or M&A assessment first?"
                }
                body={
                  index === 1
                    ? "Get a 15-minute scoping conversation with someone who has run these systems in production."
                    : "We run assessments in 2–4 weeks—tied to real cost, risk, and workflow metrics you can act on."
                }
                cta={index === 1 ? "Talk to an operator" : "Request an assessment"}
              />
            )}
          </div>
        );
      })}

      {/* ── Consolidated client outcomes ────────────────────────────────── */}
      <ClientOutcomes />

      {/* ── Final CTA ────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden border-t border-white/10 px-4 py-14 text-center lg:py-20">
        <div
          className="pointer-events-none absolute inset-0 bg-gradient-to-br from-complement-800 via-complement-950 to-[#070d11]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -right-[20%] top-1/2 h-[min(500px,55vw)] w-[min(500px,55vw)] -translate-y-1/2 rounded-full bg-complement-500/20 blur-[110px]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute -bottom-24 left-[10%] h-72 w-72 rounded-full bg-complement-400/12 blur-[80px]"
          aria-hidden
        />
        <div
          className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_90%_60%_at_50%_-20%,rgba(255,255,255,0.05),transparent_55%)]"
          aria-hidden
        />

        <motion.div
          className="relative mx-auto max-w-[600px]"
          initial={reduceMotion ? false : { opacity: 0, y: 18 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.35 }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        >
          <p className="text-[11px] font-bold uppercase tracking-[0.22em] text-complement-300/75">
            Your next step
          </p>
          <h2 className="mt-3 text-[clamp(1.625rem,3vw,2.25rem)] font-extrabold tracking-tight text-white">
            Not sure which service fits your roadmap?
          </h2>
          <p className="mt-4 text-[1.02rem] leading-relaxed text-white/60">
            Get a 15-minute walkthrough of the service mix we&apos;d recommend
            for your environment, your budget, and your timeline.
          </p>
          <div className="mt-7 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
            <Button
              asChild
              size="lg"
              className="h-[54px] border-0 bg-white px-10 text-[16px] font-bold text-complement-950 shadow-lg shadow-black/25 hover:bg-complement-50"
            >
              <Link to="/contact">
                Book Your 15-Minute Strategy Call
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
          <p className="mt-4 text-[12px] font-medium text-white/40">
            No obligation. No pitch deck. Just a conversation with an operator.
          </p>
        </motion.div>
      </section>
    </>
  );
}
